import { useMemo, useState } from 'react';
import { ArrowUpRight, Calculator as CalculatorIcon, Flame, Info, Utensils } from 'lucide-react';
import SectionHeading from './SectionHeading.jsx';

const activityMultipliers = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
  athlete: 1.9,
};

export default function Calculator() {
  const [form, setForm] = useState({
    sex: 'male',
    age: '28',
    height: '180',
    weight: '82',
    activity: 'moderate',
    goal: 'maintain',
  });
  const [submitted, setSubmitted] = useState(false);

  const result = useMemo(() => {
    const age = Number(form.age);
    const height = Number(form.height);
    const weight = Number(form.weight);

    if (!age || !height || !weight) return null;

    const base = 10 * weight + 6.25 * height - 5 * age + (form.sex === 'male' ? 5 : -161);
    let calories = base * activityMultipliers[form.activity];

    if (form.goal === 'lose') calories -= 350;
    if (form.goal === 'gain') calories += 250;

    const protein = Math.round(weight * (form.goal === 'lose' ? 2 : 1.8));
    const fat = Math.round(weight * 0.8);
    const carbohydrates = Math.max(0, Math.round((calories - protein * 4 - fat * 9) / 4));

    return {
      calories: Math.round(calories / 10) * 10,
      protein,
      fat,
      carbohydrates,
    };
  }, [form]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setSubmitted(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <section className="section section--light calculator-section">
      <div className="container calculator-layout">
        <div>
          <SectionHeading
            eyebrow="Ingyenes kalkulátor"
            title="Becsüld meg a napi energiaszükségletedet."
            text="Adj egy gyors kiindulópontot magadnak. A kalkulátor tájékoztató becslést ad, nem helyettesít személyre szabott étrendi vagy egészségügyi tanácsadást."
          />

          <div className="calculator-benefits">
            <div>
              <Flame size={20} />
              <span>Becsült napi kalória</span>
            </div>
            <div>
              <Utensils size={20} />
              <span>Kiinduló makrók</span>
            </div>
            <div>
              <Info size={20} />
              <span>Azonnali eredmény</span>
            </div>
          </div>
        </div>

        <div className="calculator-card">
          <div className="calculator-card-title">
            <CalculatorIcon size={22} />
            <h3>Kalória- és makrókalkulátor</h3>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="segmented-control" aria-label="Biológiai nem">
              <button
                type="button"
                className={form.sex === 'male' ? 'active' : ''}
                onClick={() => setForm((current) => ({ ...current, sex: 'male' }))}
              >
                Férfi
              </button>
              <button
                type="button"
                className={form.sex === 'female' ? 'active' : ''}
                onClick={() => setForm((current) => ({ ...current, sex: 'female' }))}
              >
                Nő
              </button>
            </div>

            <div className="form-grid form-grid--three">
              <label>
                <span>Életkor</span>
                <input name="age" type="number" min="16" max="90" value={form.age} onChange={updateField} required />
              </label>
              <label>
                <span>Magasság (cm)</span>
                <input name="height" type="number" min="130" max="230" value={form.height} onChange={updateField} required />
              </label>
              <label>
                <span>Testsúly (kg)</span>
                <input name="weight" type="number" min="40" max="250" value={form.weight} onChange={updateField} required />
              </label>
            </div>

            <div className="form-grid form-grid--two">
              <label>
                <span>Aktivitás</span>
                <select name="activity" value={form.activity} onChange={updateField}>
                  <option value="sedentary">Kevés mozgás</option>
                  <option value="light">Heti 1–2 edzés</option>
                  <option value="moderate">Heti 3–4 edzés</option>
                  <option value="high">Heti 5–6 edzés</option>
                  <option value="athlete">Nagyon magas aktivitás</option>
                </select>
              </label>
              <label>
                <span>Cél</span>
                <select name="goal" value={form.goal} onChange={updateField}>
                  <option value="lose">Fogyás</option>
                  <option value="maintain">Súlytartás</option>
                  <option value="gain">Izomépítés</option>
                </select>
              </label>
            </div>

            <button className="button button--dark button--full" type="submit">
              Eredmény kiszámítása
              <ArrowUpRight size={18} />
            </button>
          </form>

          {submitted && result ? (
            <div className="calculator-result" aria-live="polite">
              <div className="calorie-result">
                <span>Becsült napi cél</span>
                <strong>{result.calories.toLocaleString('hu-HU')} kcal</strong>
              </div>
              <div className="macro-grid">
                <div>
                  <span>Fehérje</span>
                  <strong>{result.protein} g</strong>
                </div>
                <div>
                  <span>Szénhidrát</span>
                  <strong>{result.carbohydrates} g</strong>
                </div>
                <div>
                  <span>Zsír</span>
                  <strong>{result.fat} g</strong>
                </div>
              </div>
              <a href="#kapcsolat">
                Személyre szabott tervet szeretnék
                <ArrowUpRight size={17} />
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
